import { saleChanges, type SaleChange, type SaleChangeKind, type SaleWriteIntent } from "./changes";
import type { Sale } from "../promotions/types";

/**
 * The effect registry (docs/architecture/SALE_EFFECTS.md §2 points 1, 4 and §6 E1a): each effect names the
 * changes it reads and turns them into a plan; the registry runs the ones a write concerns.
 *
 * Pure. A plan says WHAT to do; doing it (the network, the store, a toast) is the executor's (E1b).
 * 🔴 **Order is never behaviour**: no effect reads another's result, so the registry's order only fixes the
 * order the plans come back in. v1's effects shared a placed-order marker that one cleared before another
 * read it; a v2 effect that needs a fact about the write reads it from the change list.
 */

export interface SaleEffectInput<Context> {
  previous: Sale | null | undefined;
  next: Sale | null | undefined;
  intent: SaleWriteIntent;
  /** `saleChanges(previous, next, intent)`, computed once for every effect. */
  changes: readonly SaleChange[];
  context: Context;
}

export interface SaleEffect<Plan, Context = unknown, Id extends string = string> {
  id: Id;
  /** The change kinds that make this effect worth asking; with none of them present it is not asked. */
  reads: readonly SaleChangeKind[];
  /** The plan, or null when the effect has nothing to do for this write. */
  plan(input: SaleEffectInput<Context>): Plan | null;
}

/** One effect's plan for one write. */
export type EffectPlan<Effect> = Effect extends SaleEffect<infer Plan, any, infer Id> ? { id: Id; plan: Plan } : never;

/**
 * The plans of every effect that reads one of the write's changes and has something to do, in the order of
 * `effects`.
 */
export function runSaleEffects<Context, Effect extends SaleEffect<any, Context>>(
  previous: Sale | null | undefined,
  next: Sale | null | undefined,
  intent: SaleWriteIntent,
  effects: readonly Effect[],
  context: Context,
): EffectPlan<Effect>[] {
  const changes = saleChanges(previous, next, intent);
  const present = new Set(changes.map((change) => change.kind));
  const results: EffectPlan<Effect>[] = [];
  for (const effect of effects) {
    if (!effect.reads.some((kind) => present.has(kind))) continue;
    const plan = effect.plan({ previous, next, intent, changes, context });
    // TypeScript cannot resolve the distributive `EffectPlan<Effect>` for a generic `Effect`; the pair is exactly it.
    if (plan !== null) results.push({ id: effect.id, plan } as EffectPlan<Effect>);
  }
  return results;
}
